import React, { useContext } from 'react';
import NavDrawer from './components/common/Layout/NavDrawer';
import PageRoutes from './components/Pages/PageRoutes';
import Footer from './components/common/Layout/Footer';
import Header from './components/common/Layout/Header';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import colors from './sass/src_mui/_init/_variables.scss';
import { ConfirmProvider } from './components/common/hooks/useConfirm';
import { PageContext } from '@lib/contexts/pageContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { keyframes, styled } from '@mui/material';

const queryClient = new QueryClient();

const containedButtonStyles = (color) => {
  return {
    backgroundColor: color.main,
    border: `1px solid ${color.main}`,
    color: color.contrastText || '#fff',
    '&:hover, &:focus': {
      color: color.dark || color.main,
      background: color.contrastText || '#fff',
      border: `1px solid ${color.dark}`
    },
  };
};

const textButtonStyles = (color) => {
  return {
    color: color.main,
    '&:hover, &:focus': {
      background: `${color.main}25`,
    },
  };
};

const outlinedButtonStyles = (color) => {
  return {
    color: color.main,
    border: `1px solid ${color.main}40`,
    '&:hover, &:focus': {
      background: color.main,
      color: '#fff',
      border: `1px solid ${color.main}`,
    },
  };
};

const App = () => {
  const { pathData: { sectionName, level } } = useContext(PageContext);

  const colorPalette = Object.keys(colors).reduce((acc, key) => {
    acc[key] = { main: colors[key] };
    return acc;
  }, {
    primary: {
      main: colors['il-industrial'],
    },
    secondary: { main: colors['il-blue'] },
    hover: { main: colors['il-altgeld'] },
    text: { primary: colors['il-storm'] },
    red: { main: colors['red'] },
    blue: { main: colors['blue'] },
    lightblue: { main: '#E5F6FD' },
    ilblue: { main: colors['il-blue'] },
    ilorange: { main: colors['il-orange'] },
    ilaltgeld: { main: colors['il-altgeld'] },
    ilalma: { main: colors['il-alma'] },
    ilstorm: { main: colors['il-storm'] },
    ilindustrial: { main: colors['il-industrial'] },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#c62828',
      contrastText: '#fff',
    },
    warning: {
      main: '#ed6c02',
      light: '#ff9800',
      dark: '#e65100',
      contrastText: '#fff',
    },
    info: {
      main: '#0288d1',
      light: '#03a9f4',
      dark: '#01579b',
      contrastText: '#fff',
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
      contrastText: '#fff',
    }
  });

  let theme = createTheme({
    typography: {
      fontFamily: 'SourceSans',
      fontSize: 14,
      htmlFontSize: 16,
      h1: {
        fontFamily: 'Montserrat',
        fontWeight: 700,
        fontSize: '32px',
        lineHeight: 1.235,
        marginBottom: '21px',
        color: colorPalette.ilblue.main,
      },
      h2: {
        fontFamily: 'Montserrat',
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.334,
        letterSpacing: '0em',
      },
      h3: {
        fontFamily: 'Montserrat',
        fontWeight: 600,
        color: colorPalette.ilblue.main,
        fontSize: '1.25rem',
        lineHeight: 1.6,
        letterSpacing: '0.0075em',
      },
      h4: {
        fontFamily: 'Montserrat',
        fontWeight: 600,
        fontSize: '1.20rem',
      },
      h5: {
        fontFamily: 'Montserrat',
        fontWeight: 600,
        fontSize: '1.15rem',
      },
      h6: {
        fontFamily: 'Montserrat',
        fontWeight: 600,
        fontSize: '1rem',
      },
      h7: {
        fontFamily: 'Montserrat',
        fontWeight: 600,
        fontSize: '18px',
      }
    },
    palette: colorPalette,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        sd: 760,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    components: {
      MuiLink: {
        styleOverrides: {
          root: {
            textDecorationColor: colorPalette.ilindustrial.main,
            '&:hover': {
              cursor: 'pointer',
              color: colorPalette.hover.main,
              transition: 'color 0.3s',
            }
          }
        },
        defaultProps: {
          underline: 'always',
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: '#000',
            fontWeight: 600,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            '&:not(.MuiPickersToolbar-root)': {
              fontSize: '1rem',
              fontFamily: 'SourceSans',
              textTransform: 'none',
              borderRadius: '5px',
              padding: '0.25rem 1.3rem',
              boxShadow: 'unset',

              // Note: The official way to override these styles is to use the
              // 'variants' object, but that doesn't seem to support changing 'hover'
              // styles, so I had to fall back to using CSS selectors. - KD 2024-11-19

              // Default/Primary button style
              '&.MuiButton-contained:not(.Mui-disabled)': {
                color: '#fff',
                '&.MuiButton-colorPrimary': containedButtonStyles(colorPalette.ilblue),
                '&.MuiButton-colorSuccess': containedButtonStyles(colorPalette.success),
                '&.MuiButton-colorError': containedButtonStyles(colorPalette.error),
              },

              '&.MuiButton-text:not(.Mui-disabled)': {
                '&.MuiButton-colorPrimary': textButtonStyles(colorPalette.ilblue),
                '&.MuiButton-colorSuccess': textButtonStyles(colorPalette.success),
                '&.MuiButton-colorError': textButtonStyles(colorPalette.error),
              },

              // "Secondary" button style uses the "outlined" button variant. Not to be confused
              // with `color="secondary"` which we don't use.
              '&.MuiButton-outlined:not(.Mui-disabled)': {
                '&.MuiButton-colorPrimary': outlinedButtonStyles(colorPalette.ilblue),
                '&.MuiButton-colorSuccess': outlinedButtonStyles(colorPalette.success),
                '&.MuiButton-colorError': outlinedButtonStyles(colorPalette.error),
              },

              '&.MuiButton-contained.Mui-disabled': {
                color: '#888',
              },
              '&.Mui-disabled': {
                cursor: 'not-allowed',
                pointerEvents: 'auto',
              },

              // Styles for the "focus ripple" on the buttons. (Appears when focusing them with the tab key.)
              // See https://stackoverflow.com/questions/56169750/change-ripple-color-on-click-of-material-ui-button/56172888
              '&& .MuiTouchRipple-rippleVisible': {
                opacity: 0.2,
                animationName: keyframes`
                  0% {
                    transform: scale(0);
                    opacity: 0.1;
                  }
                  100% {
                    transform: scale(1);
                    opacity: 0.2;
                  }
                `,
              }
            },
          },
        },
      },
      MuiListItemButton: {
        defaultProps: {
          disableTouchRipple: true,
        },
      },
      MuiDateTimePickerToolbar: {
        styleOverrides: {
          timeContainer: {
            alignSelf: 'end',
            '& .MuiPickersToolbarText-root.MuiTypography-h3': {
              lineHeight: 1.1,
            }
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            background: '#fff',
          }
        }
      },
      MuiDialogContentText: {
        styleOverrides: {
          root: {
            color: colorPalette.ilstorm.main,
          }
        }
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '8px 24px 16px',
          }
        }
      },
    },
  });

  theme = createTheme(theme, {
    components: {
      MuiTabs: {
        styleOverrides: {
          root: {
            '& .MuiButtonBase-root.Mui-selected': {
              color: theme.palette.ilblue.main,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.ilblue.main,
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            // Styles for the "focus ripple" on the tab buttons. (Appears when focusing them with the tab key.)
            // See https://stackoverflow.com/questions/56169750/change-ripple-color-on-click-of-material-ui-button/56172888
            '&& .MuiTouchRipple-rippleVisible': {
              opacity: 0.2,
              animationName: keyframes`
                0% {
                  transform: scale(0);
                  opacity: 0.1;
                }
                100% {
                  transform: scale(1);
                  opacity: 0.2;
                }
              `,
            }
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            '& .MuiTableSortLabel-root.MuiButtonBase-root': {
              // add some extra space for the focus border
              paddingLeft: '8px',
              marginLeft: '-8px',
              color: '#252525 !important',
              border: '1px solid transparent',
              '&:hover .MuiTableSortLabel-icon': {
                opacity: 1,
              },
              '&:focus': {
                border: `1px solid ${theme.palette.ilblue.main}55`,
              },
            },
          }
        }
      },
      MuiList: {
        styleOverrides: {
          root: {
            '& li.MuiListItem-root:has(.other-text-input .Mui-required.Mui-error)': {
              marginBottom: '12px',
            },
          },
        },
        variants: [
          {
            props: { variant: 'nav' },
            style: {
              borderLeftColor: 'transparent',
              '& a': {
                padding: '4px 16px',
                '& .MuiListItemText-root': {
                  marginTop: 0,
                  marginBottom: 0,
                },
                '&:hover': {
                  '.MuiListItemText-root span': {
                    color: colorPalette.ilaltgeld.main,
                  }
                },
                '& .MuiListItemText-root span': {
                  color: colorPalette.ilblue.main,
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: theme.typography.pxToRem(16),
                  [theme.breakpoints.down('sm')]: {
                    fontSize: theme.typography.pxToRem(20),
                  },
                },
              },
            }
          }
        ],
      },
    }
  });

  const SkipToContent = styled('a')({
    position: 'absolute',
    left: '-9999px',
    zIndex: '1999',
    padding: '1em',
    backgroundColor: 'black',
    color: 'white',
    opacity: '0',
    tabIndex: -1,
    ':focus': {
      left: '50%',
      transform: 'translateX(-50%)',
      opacity: '1',
    },
  });

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SkipToContent
          href="#main"
          className="skip-to-main-content"
        >
          Skip to main content
        </SkipToContent>
        <NavDrawer />
        <ConfirmProvider>
          <div id="layout-container" className={'section-' + sectionName + ' lvl-' + level}
               data-section-name={sectionName}> {/*used for the expanding mobile menu*/}
            <Header />
            <main className={'bodywrapper'}>
              <PageRoutes />
            </main>
          </div>
          <Footer />
        </ConfirmProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
